import apiService from './apiService';

// Interface definitions
export interface ReleaseNote {
  id: number;
  title: string;
  description: string;
  version: string | null;
  release_date: string;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReleaseNoteCreate {
  title: string;
  description: string;
  version?: string;
  release_date?: string;
  is_visible: boolean;
}

export interface ReleaseNoteUpdate {
  title?: string;
  description?: string;
  version?: string;
  release_date?: string;
  is_visible?: boolean;
}

// Release Notes Service
const ReleaseNotesService = {
  /**
   * Get all release notes
   * @param includeHidden Optional parameter to include hidden notes (admin only)
   * @returns Promise with array of release notes
   */
  getAll: async (includeHidden: boolean = false): Promise<ReleaseNote[]> => {
    const response = await apiService.get<ReleaseNote[]>('/api/release-notes');
    return response.data;
  },

  /**
   * Get a single release note by ID
   * @param id The release note ID
   * @returns Promise with the release note
   */
  getById: async (id: number): Promise<ReleaseNote> => {
    const response = await apiService.get<ReleaseNote>(`/api/release-notes/${id}`);
    return response.data;
  },

  /**
   * Get visible release notes for public display
   * @returns Promise with array of visible release notes
   */
  getPublic: async (): Promise<ReleaseNote[]> => {
    const response = await apiService.get<ReleaseNote[]>('/api/release-notes');
    return response.data;
  },

  /**
   * Create a new release note
   * @param data The release note data
   * @returns Promise with the created release note
   */
  create: async (data: ReleaseNoteCreate): Promise<ReleaseNote> => {
    console.log('Creating release note with data:', data);
    const response = await apiService.post<ReleaseNote>('/api/release-notes', data);
    console.log('Create release note response:', response);
    return response.data;
  },

  /**
   * Update an existing release note
   * @param id The release note ID
   * @param data The data to update
   * @returns Promise with the updated release note
   */
  update: async (id: number, data: ReleaseNoteUpdate): Promise<ReleaseNote> => {
    const response = await apiService.put<ReleaseNote>(`/api/release-notes/${id}`, data);
    return response.data;
  },

  /**
   * Toggle the visibility of a release note
   * @param id The release note ID
   * @param visible The new visibility state
   * @returns Promise with the updated release note
   */
  toggleVisibility: async (id: number, visible: boolean): Promise<ReleaseNote> => {
    const response = await apiService.put<ReleaseNote>(`/api/release-notes/${id}`, { is_visible: visible });
    return response.data;
  },

  /**
   * Delete a release note
   * @param id The release note ID
   * @returns Promise with void
   */
  delete: async (id: number): Promise<void> => {
    await apiService.delete(`/api/release-notes/${id}`);
  },

  /**
   * Delete multiple release notes
   * @param ids Array of release note IDs to delete
   * @returns Promise with void
   */
  deleteMultiple: async (ids: number[]): Promise<void> => {
    await apiService.delete('/api/release-notes', { data: { ids } });
  },
};

export default ReleaseNotesService; 